import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { BlogStructureType, Company } from '../../../../@types/Company';
import { updateCompanyBranding, updateCompanyImages } from '../../../../store/AuthReducer';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization } from '../../../../utils/JWTHeader';
import FileSelector from '../../../common/FileSelector/FileSelector';
import { showToast } from '../../../common/Notification';
import Spinner from '../../../common/Spinner';
import SquareImageUploader from '../../../common/SquareImageUploader/SquareImageUploader';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label: string;
  subLabel?: string;
  id: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, label, subLabel, id }) => (
  <div className="flex w-full flex-col items-center space-y-3">
    <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
    {subLabel && <span className="text-xs text-gray-500 italic">{subLabel}</span>}
    {!subLabel && <div className="grow"></div>}
    <div className="relative flex w-full items-center justify-center">
      <input
        type="color"
        id={id}
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-24 cursor-pointer rounded-lg border-2 border-gray-200 transition-colors hover:border-gray-300 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none"
        title="Choisir une couleur"
      />
    </div>
    <div 
      className="flex h-12 w-full items-center justify-center rounded-lg font-medium text-white shadow-md transition-colors"
      style={{ backgroundColor: color }}
    >
      <span className="flex items-center gap-2">
        {label}
        <span className="text-sm opacity-80">{color.toUpperCase()}</span>
      </span>
    </div>
  </div>
);

const DEFAULT_COLORS = {
  color1: '#1a237e',
  color2: '#283593',
  color3: '#4db6ac',
};

interface CompanyBrandingEditionProps {
  showBlogStructure?: boolean;
  colorOnlyMode?: boolean;
}

interface BlogStructureOption {
  id: BlogStructureType;
  title: string;
  description: string;
}

const BLOG_STRUCTURE_OPTIONS: BlogStructureOption[] = [
  {
    id: 'straight-list',
    title: 'Liste Simple',
    description: 'Articles affichés en liste verticale classique',
  },
  {
    id: 'alternated-list',
    title: 'Liste Alternée',
    description: 'Articles alternant image à gauche et à droite',
  },
  {
    id: 'grid',
    title: 'Grille',
    description: 'Articles disposés en grille responsive',
  },
];

// Compact ColorPicker for side panel
const CompactColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, label, id }) => (
  <div className="flex w-full flex-col space-y-2">
    <label htmlFor={id} className="text-xs font-medium text-gray-700">{label}</label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        id={id}
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-8 cursor-pointer rounded border border-gray-300 transition-colors hover:border-gray-400 focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
        title="Choisir une couleur"
      />
      <div 
        className="flex h-8 flex-1 items-center justify-center rounded text-xs font-medium text-white shadow-sm"
        style={{ backgroundColor: color }}
      >
        {color.toUpperCase()}
      </div>
    </div>
  </div>
);

function CompanyBrandingEdition({ showBlogStructure = false, colorOnlyMode = false }: CompanyBrandingEditionProps) {
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.auth.company) as Company;
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [brandingImage, setBrandingImage] = useState<string | null>(null);
  const [brandingBlogImage, setBrandingBlogImage] = useState<string | null>(null);
  const [selectedStructure, setSelectedStructure] = useState<BlogStructureType>(
    (company?.brandingSettings?.blogStructure || 'straight-list') as BlogStructureType,
  );
  const [emailSubcontractorName, setEmailSubcontractorName] = useState<string>(
    company?.brandingSettings?.emailSubcontractorName || '',
  );
  const [commercialName, setCommercialName] = useState<string>(
    company?.brandingSettings?.commercialName || '',
  );

  useEffect(() => {
    if (company?.brandingSettings && !isInitialized) {
      const hasAllColors = ['color1', 'color2', 'color3'].every(
        color => company.brandingSettings[color],
      );
      
      const colorsToUse = hasAllColors ? company.brandingSettings : DEFAULT_COLORS;
      setColors(colorsToUse);
      
      if (company.brandingSettings?.brandingImageName) {
        setBrandingImage(company.brandingSettings.brandingImageName);
      }
      if (company.brandingSettings?.brandingBlogImageName) {
        setBrandingBlogImage(company.brandingSettings.brandingBlogImageName);
      }
      if (company.brandingSettings.emailSubcontractorName) {
        setEmailSubcontractorName(company.brandingSettings.emailSubcontractorName);
      }
      if (company.brandingSettings.commercialName) {
        setCommercialName(company.brandingSettings.commercialName);
      }
      setIsInitialized(true);
    }
  }, [company, isInitialized]);

  // Template fetching removed - only fixed newsletters are supported now

  const updateBrandingSettings = useCallback(async(updates: Partial<typeof company.brandingSettings>) => {
    if (!company?.id) {return;}
    
    setIsSaving(true);
    try {
      const updatedSettings = {
        color1: colors.color1,
        color2: colors.color2,
        color3: colors.color3,
        blogStructure: company.brandingSettings?.blogStructure || 'straight-list',
        referents: company.brandingSettings?.referents || [],
        emailSubcontractorName,
        commercialName,
        ...updates,
      };

      const response = await fetch(`/api/companies/${company.id}/branding-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({
          brandingSettings: updatedSettings,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save branding settings');
      }

      // Update local states based on what was updated
      // defaultTemplate handling removed - only fixed newsletters supported
      // newsletterType handling removed
      if ('blogStructure' in updates) {
        setSelectedStructure(updates.blogStructure as BlogStructureType);
      }
      if ('emailSubcontractorName' in updates) {
        setEmailSubcontractorName(updates.emailSubcontractorName as string);
      }
      if ('commercialName' in updates) {
        setCommercialName(updates.commercialName as string);
      }

      // Update Redux store with all settings
      dispatch(updateCompanyBranding({
        ...updatedSettings,
      }));

      const successMessages = {
        // defaultTemplate removed - only fixed newsletters supported
        // newsletterType removed
        // Color messages removed - handled separately now
        blogStructure: 'Structure du blog sauvegardée avec succès.',
      };

      // Only show notification for non-color updates
      const updateType = Object.keys(updates)[0] as keyof typeof successMessages;
      if (updateType && !['color1', 'color2', 'color3'].includes(updateType)) {
        showToast('success', successMessages[updateType] || 'Paramètres sauvegardés avec succès.');
      }
    } catch (error) {
      console.error('Error saving branding settings :', error);
      showToast('error', 'Échec de la sauvegarde des paramètres');
      
      // Revert local states on error
      // defaultTemplate revert removed - only fixed newsletters supported
      // newsletterType revert removed
      if ('blogStructure' in updates) {
        setSelectedStructure((company.brandingSettings?.blogStructure || 'straight-list') as BlogStructureType);
      }
      // Color revert logic removed - handled in the separate color function
      if ('emailSubcontractorName' in updates) {
        setEmailSubcontractorName(company.brandingSettings?.emailSubcontractorName || '');
      }
      if ('commercialName' in updates) {
        setCommercialName(company.brandingSettings?.commercialName || '');
      }
      
    } finally {
      setIsSaving(false);
    }
  }, [company?.id, company?.brandingSettings, colors, emailSubcontractorName, commercialName, jwt, dispatch]);

  const handleStructureChange = useCallback((structureId: BlogStructureType) => {
    void updateBrandingSettings({ blogStructure: structureId });
  }, [updateBrandingSettings]);

  // Newsletter type selection removed - only fixed newsletters supported

  const saveColorsToBackend = useCallback(async(newColors: typeof colors) => {
    if (!company?.id) {
      showToast('error', 'ID de l\'entreprise non trouvé.');
      return;
    }

    // Check if colors have actually changed from company's current colors
    const hasColorsChanged = company?.brandingSettings ? (
      newColors.color1 !== company.brandingSettings.color1 ||
      newColors.color2 !== company.brandingSettings.color2 ||
      newColors.color3 !== company.brandingSettings.color3
    ) : true;

    if (!hasColorsChanged) {
      return; // Don't save if colors haven't changed
    }

    setIsSaving(true);
    try {
      const updatedSettings = {
        color1: newColors.color1,
        color2: newColors.color2,
        color3: newColors.color3,
        blogStructure: company.brandingSettings?.blogStructure || 'straight-list',
        referents: company.brandingSettings?.referents || [],
        emailSubcontractorName,
        commercialName,
      };

      const response = await fetch(`/api/companies/${company.id}/branding-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({
          brandingSettings: updatedSettings,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save branding settings');
      }

      // Show success notification only after successful API call
      showToast('success', 'Couleurs sauvegardées avec succès.');
    } catch (error) {
      console.error('Error saving branding settings :', error);
      showToast('error', 'Échec de la sauvegarde des paramètres');
      
      // Revert local states and store on error
      setColors({
        color1: company.brandingSettings?.color1 || DEFAULT_COLORS.color1,
        color2: company.brandingSettings?.color2 || DEFAULT_COLORS.color2,
        color3: company.brandingSettings?.color3 || DEFAULT_COLORS.color3,
      });
      
      // Revert store to original colors
      dispatch(updateCompanyBranding({
        color1: company.brandingSettings?.color1 || DEFAULT_COLORS.color1,
        color2: company.brandingSettings?.color2 || DEFAULT_COLORS.color2,
        color3: company.brandingSettings?.color3 || DEFAULT_COLORS.color3,
      }));
    } finally {
      setIsSaving(false);
    }
  }, [company?.id, company?.brandingSettings, emailSubcontractorName, commercialName, jwt, dispatch]);

  // Debounced Redux store update for live preview (separate from backend save)
  const debouncedUpdateReduxStore = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return (newColors: typeof colors) => {
        if (!company?.id) {return;} // Don't schedule update if no company ID
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          // Update Redux store for live preview in other components
          dispatch(updateCompanyBranding(newColors));
        }, 600); // Shorter debounce for live preview (600ms)
      };
    })(),
    [company?.id, dispatch],
  );

  // Debounced backend save (longer debounce)
  const debouncedSaveColorsToBackend = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return (newColors: typeof colors) => {
        if (!company?.id) {return;} // Don't schedule save if no company ID
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveColorsToBackend(newColors);
        }, 1500); // 600ms as requested for backend save
      };
    })(),
    [company?.id, saveColorsToBackend],
  );

  const handleColorChange = (newColor: string, colorKey: 'color1' | 'color2' | 'color3') => {
    const newColors = {
      ...colors,
      [colorKey]: newColor,
    };
    
    // 1. Update local state immediately for instant UI feedback
    setColors(newColors);
    
    // 2. Debounce Redux store update for live preview (shorter delay for better UX)
    debouncedUpdateReduxStore(newColors);
    
    // 3. Debounce the backend API call and notification (longer delay to prevent spam)
    debouncedSaveColorsToBackend(newColors);
  };

  // Auto-save default colors if they're not set in the company branding settings
  useEffect(() => {
    if (isInitialized && company?.brandingSettings && company?.id) {
      const hasAllColors = ['color1', 'color2', 'color3'].every(
        color => company.brandingSettings[color],
      );
      
      // If colors are missing, save the current default colors directly to backend
      if (!hasAllColors) {
        void saveColorsToBackend(colors);
      }
    }
  }, [isInitialized, company?.brandingSettings, company?.id, colors.color1, colors.color2, colors.color3, saveColorsToBackend]);

  const handleImageUpload = async(event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {return;}

    const formData = new FormData();
    formData.append('file', file);

    setIsSaving(true);
    try {
      const response = await fetch(`/api/companies/${company.id}/branding-image`, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error((errorData && errorData.errors && errorData.errors.file) || 'Une erreur est survenue lors du téléchargement de l\'image');
      }

      const data = await response.json();
      const uploadedImage = data?.brandingSettings?.brandingImageName;
      setBrandingImage(uploadedImage);
      
      // Update Redux store with new branding image
      dispatch(updateCompanyImages({
        brandingImageName: uploadedImage,
      }));
      
      showToast('success', 'Image téléchargée avec succès.');
    } catch (error) {
      console.error('Error uploading image :', error);
      showToast('error', error instanceof Error ? error.message : 'Échec du téléchargement de l\'image.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailSubcontractorNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailSubcontractorName(event.target.value);
  }, []);

  const handleSaveEmailSubcontractorName = useCallback(() => {
    if (!company?.id) {
      showToast('error', 'ID de l\'entreprise non trouvé.');
      return;
    }
    void updateBrandingSettings({ emailSubcontractorName });
  }, [company?.id, emailSubcontractorName, updateBrandingSettings]);

  const handleCommercialNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCommercialName(event.target.value);
  }, []);

  const handleSaveCommercialName = useCallback(() => {
    if (!company?.id) {
      showToast('error', 'ID de l\'entreprise non trouvé.');
      return;
    }
    void updateBrandingSettings({ commercialName });
  }, [company?.id, commercialName, updateBrandingSettings]);

  if (!company?.id) {
    return (
      <div className="flex items-center justify-center rounded-lg bg-white p-8 shadow-md">
        <Spinner size="xl" />
      </div>
    );
  }

  // Color-only mode for side panel
  if (colorOnlyMode) {
    return (
      <div className="rounded-lg bg-white p-4 shadow-md">
        <h3 className="mb-4 text-sm font-semibold text-gray-800">Couleurs de l'entreprise</h3>
        <div className="space-y-4">
          <CompactColorPicker
            id="color1-compact"
            color={colors.color1}
            onChange={(color) => handleColorChange(color, 'color1')}
            label="Couleur principale"
          />
          <CompactColorPicker
            id="color2-compact"
            color={colors.color2}
            onChange={(color) => handleColorChange(color, 'color2')}
            label="Couleur secondaire"
          />
          <CompactColorPicker
            id="color3-compact"
            color={colors.color3}
            onChange={(color) => handleColorChange(color, 'color3')}
            label="Couleur accent"
          />
        </div>
        {isSaving && (
          <div className="mt-3 flex items-center justify-center text-xs text-gray-500">
            <Spinner size="sm" className="mr-2" />
            Sauvegarde...
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-8 shadow-md">
      <h2 className="mb-8 text-2xl font-bold text-gray-800">Personnalisation de la marque</h2>
      
      {/* Commercial Name Section */}
      <div className="mb-12">
        <h3 className="mb-6 text-lg font-semibold text-gray-700">Nom commercial de l'entreprise</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="commercialName" className="mb-1 block text-sm font-medium text-gray-700">
              Nom commercial (optionnel)
            </label>
            <input
              type="text"
              id="commercialName"
              value={commercialName}
              onChange={handleCommercialNameChange}
              placeholder="Laissez vide pour utiliser le nom légal"
              className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-transparent focus:ring-2 focus:ring-rgpd-blue"
              disabled={isSaving}
            />
            <p className="mt-2 text-sm text-gray-500">
              Si renseigné, ce nom sera utilisé à la place du nom légal ({company.name}) dans les newsletters, le blog, et toutes les communications destinées à vos clients.
            </p>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleSaveCommercialName}
              disabled={isSaving}
              className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors duration-200 hover:bg-rgpd-blue-dark disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Sauvegarde...
                </>
              ) : (
                'Sauvegarder'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Logos Upload Section */}
      <div className="mb-12 border-t pt-8">
        <h3 className="mb-2 text-lg font-semibold text-gray-700">Logos de l'entreprise</h3>
        <p className="mb-6 text-sm text-gray-500">
          Deux logos peuvent être configurés : un logo principal au format paysage utilisé dans vos
          newsletters et communications, et un logo carré dédié à votre blog public.
        </p>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Main landscape logo */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-1 text-base font-semibold text-gray-800">Logo principal</h4>
            <p className="mb-4 text-xs text-gray-500">
              Utilisé dans vos newsletters et la majorité de vos communications. Un format paysage
              (plus large que haut) est recommandé pour un rendu optimal.
            </p>
            <div className="space-y-4">
              {brandingImage && (
                <div className="flex flex-col items-center gap-2 rounded-lg bg-gray-50 p-3">
                  <img
                    src={brandingImage}
                    alt="Logo principal de l'entreprise"
                    className="h-24 w-48 rounded border bg-white object-contain p-2 shadow-sm"
                  />
                  <p className="text-xs text-gray-500">Logo actuel — paysage</p>
                </div>
              )}
              <div className="flex items-center justify-center">
                <FileSelector
                  accept="image/jpeg, image/png"
                  onChange={handleImageUpload}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>

          {/* Square blog logo */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-1 text-base font-semibold text-gray-800">Logo carré pour le blog</h4>
            <p className="mb-4 text-xs text-gray-500">
              Utilisé sur votre blog public. L'image sera recadrée au format carré si nécessaire.
            </p>
            {company?.id ? (
              <SquareImageUploader
                currentImage={brandingBlogImage}
                uploadUrl={`/api/companies/${company.id}/branding-blog-image`}
                jwt={jwt}
                extractPath={(data) => (data as { brandingSettings?: { brandingBlogImageName?: string } } | null)?.brandingSettings?.brandingBlogImageName}
                onUploaded={(path) => {
                  setBrandingBlogImage(path);
                  dispatch(updateCompanyImages({ brandingBlogImageName: path }));
                }}
                previewAlt="Logo blog de l'entreprise"
                previewCaption="Logo actuel — carré"
                previewSizeClass="h-28 w-28"
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Email Delivery Subcontractor Section */}
      <div className="mb-12 border-t pt-8">
        <h3 className="mb-6 text-lg font-semibold text-gray-700">Sous-traitant d'envoi de mail</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="emailSubcontractorName" className="mb-1 block text-sm font-medium text-gray-700">
                Nom de votre sous-traitant d'envoi de mail
              </label>
              <input
                type="text"
                id="emailSubcontractorName"
                value={emailSubcontractorName}
                onChange={handleEmailSubcontractorNameChange}
                placeholder="Nom de votre sous-traitant d'envoi de mail"
                className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-transparent focus:ring-2 focus:ring-rgpd-blue"
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleSaveEmailSubcontractorName}
              disabled={isSaving}
              className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors duration-200 hover:bg-rgpd-blue-dark disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Sauvegarde...
                </>
              ) : (
                'Sauvegarder'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Color Pickers Section */}
      <div className="mb-12 border-t pt-8">
        <h3 className="mb-6 text-lg font-semibold text-gray-700">Couleurs de l'entreprise</h3>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <ColorPicker
            id="color1"
            color={colors.color1}
            onChange={(color) => handleColorChange(color, 'color1')}
            label="Couleur principale"
            subLabel="(couleur de votre blog)"
          />
          <ColorPicker
            id="color2"
            color={colors.color2}
            onChange={(color) => handleColorChange(color, 'color2')}
            label="Couleur secondaire"
          />
          <ColorPicker
            id="color3"
            color={colors.color3}
            onChange={(color) => handleColorChange(color, 'color3')}
            label="Couleur accent"
          />
        </div>
        {isSaving && (
          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <Spinner size="sm" className="mr-2" />
            Sauvegarde en cours...
          </div>
        )}
      </div>

      {/* Newsletter Template Section removed - only fixed newsletters supported */}

      {/* Blog Structure Section */}
      {showBlogStructure && (
        <div className="border-t pt-8">
          <h3 className="mb-6 text-lg font-semibold text-gray-700">Structure du Blog</h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {BLOG_STRUCTURE_OPTIONS.map((option) => (
              <div
                key={option.id}
                onClick={() => handleStructureChange(option.id)}
                className={`
                  cursor-pointer rounded-lg border-2 p-6 transition-all
                  ${selectedStructure === option.id 
                ? 'border-rgpd-blue bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
              }
                `}
              >
                <h4 className="mb-2 font-medium text-gray-800">{option.title}</h4>
                <p className="mb-6 text-sm text-gray-600">{option.description}</p>
                
                {/* Preview UI */}
                <div className="rounded-lg bg-white p-4 shadow-sm">
                  {option.id === 'straight-list' && (
                    <div className="space-y-6">
                      {[1, 2].map((i) => (
                        <div 
                          key={i} 
                          className={`flex gap-4`}
                        >
                          <div className="h-24 w-1/3 shrink-0 rounded-lg bg-gray-200"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-1/2 rounded bg-gray-700"></div>
                            <div className="h-3 w-full rounded bg-gray-300"></div>
                            <div className="h-3 w-3/4 rounded bg-gray-300"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {option.id === 'alternated-list' && (
                    <div className="space-y-6">
                      {[1, 2].map((i) => (
                        <div 
                          key={i} 
                          className={`flex ${i % 2 === 0 ? 'flex-row-reverse' : ''} gap-4`}
                        >
                          <div className="h-24 w-1/3 shrink-0 rounded-lg bg-gray-200"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-1/2 rounded bg-gray-700"></div>
                            <div className="h-3 w-full rounded bg-gray-300"></div>
                            <div className="h-3 w-3/4 rounded bg-gray-300"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {option.id === 'grid' && (
                    <div className="grid grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="space-y-2">
                          <div className="h-20 w-full rounded-lg bg-gray-200"></div>
                          <div className="h-3 w-2/3 rounded bg-gray-700"></div>
                          <div className="h-2 w-full rounded bg-gray-300"></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {isSaving && (
            <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
              <Spinner size="sm" className="mr-2" />
              Sauvegarde en cours...
            </div>
          )}
          
          <div className="mt-8 flex justify-center">
            <a
              href={`/blog/company/${company.siren}`}
              target="_blank"
              className="inline-flex items-center rounded-lg bg-rgpd-blue px-6 py-3 font-medium text-white transition-colors duration-200 hover:bg-rgpd-blue-dark"
            >
              Accéder à mon blog personnalisé
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyBrandingEdition; 